'use client';

import React, { useEffect, useState } from 'react';

export default function SoundWaveEntrance() {
  const [phase, setPhase] = useState(0); // 0 = visible, animating down
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Start the wave travel animation
    const animFrame = requestAnimationFrame(() => {
      setPhase(1);
    });

    // Remove overlay after animation completes
    const timer = setTimeout(() => {
      setDone(true);
    }, 1800);

    return () => {
      cancelAnimationFrame(animFrame);
      clearTimeout(timer);
    };
  }, []);

  if (done) return null;

  return (
    <div
      className="sound-wave-entrance"
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        style={{ position: 'absolute', inset: 0 }}
      >
        <defs>
          {/* Teal glow gradient matching brand */}
          <linearGradient id="waveGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0d9488" stopOpacity="0" />
            <stop offset="20%" stopColor="#2dd4bf" stopOpacity="0.9" />
            <stop offset="50%" stopColor="#5eead4" stopOpacity="1" />
            <stop offset="80%" stopColor="#2dd4bf" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#0d9488" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="waveGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#0f766e" stopOpacity="0" />
            <stop offset="25%" stopColor="#14b8a6" stopOpacity="0.6" />
            <stop offset="50%" stopColor="#2dd4bf" stopOpacity="0.8" />
            <stop offset="75%" stopColor="#14b8a6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#0f766e" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="waveGrad3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#134e4a" stopOpacity="0" />
            <stop offset="30%" stopColor="#0d9488" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#14b8a6" stopOpacity="0.5" />
            <stop offset="70%" stopColor="#0d9488" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#134e4a" stopOpacity="0" />
          </linearGradient>
          <filter id="waveGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Wave 1 — primary, tightest bend */}
        <path
          d="M -80,0 C 120,-30 200,60 360,20 C 520,-20 600,80 760,30 C 920,-20 1000,70 1160,15 C 1320,-40 1400,50 1520,0"
          fill="none"
          stroke="url(#waveGrad1)"
          strokeWidth="2.5"
          filter="url(#waveGlow)"
          style={{
            animation: 'waveTravelDown 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
          }}
        />

        {/* Wave 2 — secondary, wider bend, slight offset */}
        <path
          d="M -80,0 C 100,-50 220,80 400,25 C 580,-30 680,90 860,35 C 1040,-20 1140,75 1320,10 C 1420,-30 1480,40 1540,0"
          fill="none"
          stroke="url(#waveGrad2)"
          strokeWidth="1.8"
          filter="url(#waveGlow)"
          style={{
            animation: 'waveTravelDown 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.06s forwards',
          }}
        />

        {/* Wave 3 — tertiary, broadest refraction arc */}
        <path
          d="M -80,0 C 80,-70 240,100 440,30 C 640,-40 760,100 960,40 C 1160,-20 1280,80 1460,5 C 1520,-20 1540,20 1560,0"
          fill="none"
          stroke="url(#waveGrad3)"
          strokeWidth="1.2"
          filter="url(#waveGlow)"
          style={{
            animation: 'waveTravelDown 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.12s forwards',
          }}
        />

        {/* Wave 4 — echo, very faint */}
        <path
          d="M -80,0 C 140,-20 180,50 340,15 C 500,-20 560,60 720,20 C 880,-20 960,55 1120,10 C 1280,-35 1360,45 1540,0"
          fill="none"
          stroke="url(#waveGrad2)"
          strokeWidth="1"
          style={{
            opacity: 0.35,
            animation: 'waveTravelDown 1.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s forwards',
          }}
        />
      </svg>

      <style>{`
        @keyframes waveTravelDown {
          0% {
            transform: translateY(-20px);
            opacity: 0;
          }
          8% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            transform: translateY(920px);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}
